import apiClient from './client';

/**
 * MED-1389: a booking made through a group booking link that did not attach
 * to a session. It holds the coach's time and sent an invite. Staff see the
 * cohort's; MED-1399: a coach sees the bookings on their own links.
 */
export type UnattachedBookingOutcome =
  | 'unknown_email'
  | 'not_in_group'
  | 'already_scheduled'
  | 'no_unscheduled_session';

export interface UnattachedPublicBooking {
  id: string;
  scheduled_start: string;
  duration_minutes: number;
  attach_outcome: UnattachedBookingOutcome;
  /** unconfirmed: a later step failed, so the event may not exist. */
  status: 'scheduled' | 'unconfirmed';
  booker_name: string;
  booker_email: string;
  /** MED-1417: the member the email matched, when one did. */
  matched_user_id: string | null;
  /** The matched member's name, for staff only. The booker can type a different one. */
  matched_user_name: string | null;
  coach_name: string | null;
  group_id: string;
  group_name: string;
  touchpoint: 'intro' | 'midpoint_checkin' | 'one_on_one';
  touchpoint_label: string;
  booked_with: 'calendly' | 'google_calendar';
  /**
   * MED-1399 QA round 2: the member's upcoming booking that "Replace current
   * booking" would cancel, or null when the row offers no replace.
   */
  replace: ReplaceTarget | null;
}

/** MED-1399 QA round 2: the booking a replace cancels. */
export interface ReplaceTarget {
  session_id: string;
  scheduled_start: string;
  coach_name: string | null;
  member_name: string | null;
}

/** MED-1399: a member that the API can link a booking to. */
export interface LinkableMember {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  group_name: string;
}

export const cohortPublicBookingsApi = {
  /**
   * ownOnly: only the bookings on the caller's own links. The coach page
   * sends it, so a staff user there sees the same list as a coach.
   */
  async listUnattached(
    cohortId: string,
    ownOnly = false
  ): Promise<UnattachedPublicBooking[]> {
    return apiClient.get<UnattachedPublicBooking[]>(
      `/api/cohorts/${cohortId}/public-bookings${ownOnly ? '?own=true' : ''}`
    );
  },

  /** MED-1399: the members this booking can be linked to, by name. */
  async linkableMembers(
    cohortId: string,
    bookingId: string
  ): Promise<LinkableMember[]> {
    return apiClient.get<LinkableMember[]>(
      `/api/cohorts/${cohortId}/public-bookings/${bookingId}/members`
    );
  },

  async cancel(
    cohortId: string,
    bookingId: string
  ): Promise<{ id: string; status: 'canceled' }> {
    return apiClient.post<{ id: string; status: 'canceled' }>(
      `/api/cohorts/${cohortId}/public-bookings/${bookingId}/cancel`
    );
  },

  /**
   * Remove a booking from the list without a vendor cancel. The API allows it
   * only when Calendly refuses to show the booking (403 or 404).
   */
  async remove(
    cohortId: string,
    bookingId: string
  ): Promise<{ id: string; status: 'canceled' }> {
    return apiClient.post<{ id: string; status: 'canceled' }>(
      `/api/cohorts/${cohortId}/public-bookings/${bookingId}/remove`
    );
  },

  /**
   * MED-1399: link the booking to a cohort member. It fills the member's
   * earliest open session of the booking's kind. Staff, or the coach of the
   * booking link. No email and no calendar change.
   */
  async link(
    cohortId: string,
    bookingId: string,
    participantId: string
  ): Promise<{ id: string; session_id: string }> {
    return apiClient.post<{ id: string; session_id: string }>(
      `/api/cohorts/${cohortId}/public-bookings/${bookingId}/link`,
      { participant_id: participantId }
    );
  },

  /**
   * MED-1417: add a 1:1 session for a member with no open 1:1 of this kind,
   * and link the booking to it. Staff only. No email and no calendar change.
   */
  async addSession(
    cohortId: string,
    bookingId: string,
    participantId: string
  ): Promise<{ id: string; session_id: string }> {
    return apiClient.post<{ id: string; session_id: string }>(
      `/api/cohorts/${cohortId}/public-bookings/${bookingId}/add-session`,
      { participant_id: participantId }
    );
  },

  /**
   * MED-1399 QA round 2: cancel the member's current booking (the one the
   * list named) and put this booking on its session. The API refuses it if
   * that booking changed since the list.
   */
  async replace(
    cohortId: string,
    bookingId: string,
    target: Pick<ReplaceTarget, 'session_id' | 'scheduled_start'>
  ): Promise<{ id: string; session_id: string }> {
    return apiClient.post<{ id: string; session_id: string }>(
      `/api/cohorts/${cohortId}/public-bookings/${bookingId}/replace`,
      {
        session_id: target.session_id,
        scheduled_start: target.scheduled_start,
      }
    );
  },
};

export default cohortPublicBookingsApi;
